import React, {useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux'
import {MyInput, utils} from '../../utils/utils'
import {getPersistentStorage, getSession, setAnimated, setCurrBildIdx, setEditMode, setPointSelection, setRotated, updateBild, updatePlan} from '../../utils/redux'
import Point from '../components/point'
import Wheel from '../components/wheel'
import DistDlg from '../components/distDlg'
import {BildTable} from '../components/table'
import {openDialogView} from '../components/dialog';

const CHAR_WIDTH_PX = 9 // grobe Schaetzung der Zeichenbreite bei 16px Schrift
const LABEL_MARGIN = 4 // zusaetzlicher Sicherheitsabstand zwischen zwei Beschriftungen in px

// Verteilt Werte reihum (0,1,2,...,0,1,2,...) auf so viele Zeilen/Spalten wie noetig, damit
// zwei Werte in derselben Zeile/Spalte (samt ihres Platzbedarfs) sich nie ueberlappen. Startet
// bei 2 Zeilen/Spalten und erhoeht die Anzahl, bis der noetige Abstand ueberall eingehalten ist.
const distributeRoundRobin = (vals, unit, requiredGapPx) => {
    const sorted = [...vals].sort((a, b) => a - b)
    if (sorted.length === 0) return {levelByValue: new Map(), levelCount: 0}

    // Prueft nur, ob k Zeilen/Spalten fuer diese Werte kollisionsfrei ausreichen wuerden
    const fits = (k) => {
        for (let level = 0; level < k; level++) {
            let lastPx = null
            for (let i = level; i < sorted.length; i += k) {
                const px = sorted[i] * unit
                if (lastPx !== null && px - lastPx < requiredGapPx(sorted[i]) + LABEL_MARGIN) return false
                lastPx = px
            }
        }
        return true
    }

    let k = 2
    while (k < 6 && !fits(k)) k++

    // Weist unabhaengig vom fits()-Ergebnis IMMER jedem Wert eine Zeile/Spalte zu (i % k),
    // notfalls mit Rest-Ueberlappung bei k=6 - sonst fehlen einzelnen Werten Eintraege und
    // ihre Position wird beim Rendern zu NaN
    const levelByValue = new Map()
    sorted.forEach((v, i) => levelByValue.set(v, i % k))
    return {levelByValue, levelCount: k}
}

const xGap = v => String(v).length * CHAR_WIDTH_PX // Beschriftung ist zentriert -> volle Textbreite noetig

const TOP_ROW_HEIGHT = 20 // px Abstand zwischen zwei Zeilen der X-Beschriftung (etwas mehr als die 18px Boxhoehe)
const SWIPE_THRESHOLD_PX = 60 // Mindest-Wischweite, damit ein Wisch als Bildwechsel gilt

const LABEL_BOX_PX = 20 // Breite/Hoehe der .markerTop/.markerLeft-Box, per CSS translateX(-50%)/translateY(-50%) um ihre eigene Position zentriert

// Die H/D-Randbeschriftung sitzt an einer festen Position am Rand der Y-Spalte (Zentrum bei 5px
// bzw. bei rotated am gegenueberliegenden Rand bei containerSize-20px, siehe JSX-Styles unten).
// Liegt zufaellig ein echter Wert nahe an diesem Zentrum, wuerde er die Beschriftung ueberlappen -
// dann lieber ausblenden statt die Beschriftung zu verschieben
const edgeLabelCollides = (vals, unit, rotated) => {
    if (vals.length === 0) return false
    const containerSize = 16 * unit
    const labelCenter = rotated ? containerSize - LABEL_BOX_PX : 5
    const nearestValueCenter = rotated
        ? Math.max(...vals.map(v => (v + 8) * unit))
        : Math.min(...vals.map(v => (v + 8) * unit))
    return Math.abs(nearestValueCenter - labelCenter) < LABEL_BOX_PX
}

export default function TabBild(props) {
    const dispatch = useDispatch();
    const glob = {...useSelector(getPersistentStorage), ...useSelector(getSession)};
    const currBildIdx = glob.currBildIdx
    const currBild = glob.plan.bilder[currBildIdx];
    const [inp, setInp] = useState('')
    const pointerStart = useRef(null)
    if (!currBild) return null

    const xVals = Array.from(new Set(currBild.leaders.map(p => p[0])))
    const yVals = Array.from(new Set(currBild.leaders.map(p => p[1])))

    let originalBild = glob.planOriginal.bilder[currBildIdx]
    if (!originalBild || originalBild.id !== currBild.id)
        originalBild = glob.planOriginal.bilder.find(b => b.id === currBild.id)
    const changed = originalBild && !utils.compareRec(currBild, originalBild)


    let xVals2 = []
    let yVals2 = []
    if (currBild.followers) {
        let sameX = true
        let sameY = true
        currBild.leaders.forEach((p, i) => {
            if (currBild.followers[i][0] !== p[0]) sameX = false
            if (currBild.followers[i][1] !== p[1]) sameY = false
        })
        if (!sameX) {
            xVals2 = Array.from(new Set(currBild.followers.map(p => p[0])))
        }
        if (!sameY) {
            yVals2 = Array.from(new Set(currBild.followers.map(p => p[1])))
        }
    }

    // Herren-Meter oberhalb des Grids, Damen-Meter unterhalb - beide unabhaengig voneinander verteilt
    const xLevels = distributeRoundRobin(xVals, glob.unit, xGap)
    const x2Levels = distributeRoundRobin(xVals2, glob.unit, xGap)
    // H sitzt oben UND links vom Grid, jeweils in der oberen Ecke (Seite je nach "gedreht"-Status).
    // D (Damen-Y) sitzt weiterhin links vom Grid in derselben oberen Ecke wie H, D (Damen-Meter)
    // sitzt jetzt unterhalb des Grids, also in der jeweils gegenueberliegenden (unteren) Ecke.
    // Liegt ein echter Wert - auf der eigenen ODER der jeweils anderen Achse - nahe an der
    // betroffenen Ecke, wird die Beschriftung ausgeblendet, statt sich mit dem Wert zu ueberlappen
    const hHidden = edgeLabelCollides(xVals, glob.unit, glob.rotated) || edgeLabelCollides(yVals, glob.unit, glob.rotated)
    const dHiddenLeft = edgeLabelCollides(yVals2, glob.unit, glob.rotated) || edgeLabelCollides(xVals2, glob.unit, glob.rotated)
    const dHiddenBottom = edgeLabelCollides(xVals2, glob.unit, glob.rotated) || edgeLabelCollides(yVals2, glob.unit, !glob.rotated)
    // Platz oberhalb/unterhalb des Grids fuer die Meter-Beschriftungen, damit die aeusserste Zeile
    // nicht mit der Bildnavigation/-ueberschrift darueber bzw. dem Inhalt darunter ueberlappt.
    const gridTopMargin = 30 + xLevels.levelCount * TOP_ROW_HEIGHT
    const gridBottomMargin = 30 + x2Levels.levelCount * TOP_ROW_HEIGHT

    const select = (val, axis, part) => {
        let selected = [...glob.selectedPoints]
        let vals = []
        let deselect = true
        let points = part === 2 ? currBild.leaders : currBild.followers
        points.forEach((p, i) => {
            if (p[axis] === val) {
                let idx = part === 2 ? i : i + 8
                vals.push(idx)
                if (!selected[idx]) deselect = false
            }
        })
        setPointSelection(vals, !deselect)

    }

    const selectedPoints = [...currBild.leaders.filter((x, i) => glob.selectedPoints[i]), ...(currBild.followers ?? []).filter((x, i) => glob.selectedPoints[i + 8])]
    const selectedXVals = Array.from(new Set(selectedPoints.map(p => p[0]))).toSorted((x, y) => x - y)
    const selectedYVals = Array.from(new Set(selectedPoints.map(p => p[1]))).toSorted((x, y) => x - y)

    const sameX = selectedXVals.length > 2 && new Set(selectedXVals.map((x, i) => i === 0 ? 0 : x - selectedXVals[i - 1]).slice(1)).size === 1
    const sameY = selectedYVals.length > 2 && new Set(selectedYVals.map((x, i) => i === 0 ? 0 : x - selectedYVals[i - 1]).slice(1)).size === 1

    const selectedPos = []
    for (let i = 0; i < 8; i++) if (glob.selectedPoints[i] || (currBild.followers && glob.selectedPoints[i + 8])) selectedPos.push(i)

    const swapPoints = () => {
        let positions = Object.keys(glob.selectedPoints).filter(x => glob.selectedPoints[x])
        let bild = structuredClone(currBild)
        let arr1 = positions[0] < 8 ? bild.leaders : bild.followers
        let arr2 = positions[1] < 8 ? bild.leaders : bild.followers
        let [p1, p2] = [arr1[positions[0] % 8], arr2[positions[1] % 8]]
        arr1[positions[0] % 8] = p2
        arr2[positions[1] % 8] = p1
        updateBild(bild)
    }


    const swapPointsFromHere = () => {
        let plan = structuredClone(glob.plan)
        for (let i = currBildIdx; i < plan.bilder.length; i++) {
            let bild = plan.bilder[i]
            let temp = bild.leaders[selectedPos[0]]
            bild.leaders[selectedPos[0]] = bild.leaders[selectedPos[1]]
            bild.leaders[selectedPos[1]] = temp
            if (bild.followers) {
                temp = bild.followers[selectedPos[0]]
                bild.followers[selectedPos[0]] = bild.followers[selectedPos[1]]
                bild.followers[selectedPos[1]] = temp
            }
            plan.bilder[i] = bild
        }
        updatePlan(plan)
    }

    const deleteBild = () => {
        let plan = structuredClone(glob.plan)
        plan.bilder.splice(currBildIdx, 1)
        updatePlan(plan)
        if (currBildIdx >= plan.bilder.length)
            dispatch(setCurrBildIdx(plan.bilder.length - 1))
    }

    const copyFrom = () => {
        let bild = glob.plan.bilder[inp - 1]
        if (!bild) alert('Ungültige Eingabe')
        updateBild({...currBild, leaders: bild.leaders, followers: bild.followers})
    }

    const toggleFollowers = () => {
        updateBild({...currBild, followers: currBild.followers ? undefined : structuredClone(currBild.leaders)})
    }

    const handlePointerDown = e => {
        pointerStart.current = {x: e.clientX, y: e.clientY}
    }
    const handlePointerUp = e => {
        if (!pointerStart.current) return
        const dx = e.clientX - pointerStart.current.x
        const dy = e.clientY - pointerStart.current.y
        pointerStart.current = null
        // Nur werten, wenn deutlich mehr horizontal als vertikal bewegt wurde (kein Scroll-Swipe)
        if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return
        if (dx < 0 && currBildIdx < glob.plan.bilder.length - 1) dispatch(setCurrBildIdx(currBildIdx + 1))
        else if (dx > 0 && currBildIdx > 0) dispatch(setCurrBildIdx(currBildIdx - 1))
    }

    let freeCorners = [true, true, true, true]
    let points = [...currBild.leaders, ...(currBild.followers ?? [])]
    points.forEach(p => {
        if (p[0] < -6 && p[1] < -6) freeCorners[0] = false
        if (p[0] > 6 && p[1] < -6) freeCorners[1] = false
        if (p[0] > 6 && p[1] > 6) freeCorners[2] = false
        if (p[0] < -6 && p[1] > 6) freeCorners[3] = false
    })
    if (glob.rotated) freeCorners = [freeCorners[2], freeCorners[3], freeCorners[0], freeCorners[1]]
    let btnStyle = {right: '0px', top: '0px'}
    if (freeCorners[1]) {
    } else if (freeCorners[0]) btnStyle = {left: '0px', top: '0px'}
    else if (freeCorners[2]) btnStyle = {right: '0px', bottom: '0px'}
    else if (freeCorners[3]) btnStyle = {left: '0px', bottom: '0px'}

    let followers = currBild.followers
    if (!followers && glob.animated && currBildIdx > 0) {
        followers = glob.plan.bilder[currBildIdx - 1].followers;

    }


    return (<div id="tabBild" style={{padding: '0px 20px', touchAction: 'pan-y'}} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}>
        <BildHeader/> {/* Zeile mit Title, Navigation zu anderen Bildern, editMode, etc.*/}
        <div style={{textAlign: 'center'}}>
            {glob.editMode ? <div style={{marginTop: '10px', marginBottom: '10px'}}>
                <button style={{color: 'red'}} onClick={deleteBild}>Bild löschen</button>
            </div> : null}
            {glob.editMode ? <div style={{marginBottom: '10px'}}>
                <button disabled={!changed} onClick={() => updateBild(originalBild)}>Änderungen in diesem Bild verwerfen</button>
            </div> : null}
            <div id="gridWrapper" className={(glob.rotated ? "rotated " : "") + (yVals2.length > 0 ? "hasFollowerY" : "")} style={{display: 'inline-block', marginTop: gridTopMargin + 'px', marginBottom: gridBottomMargin + 'px', position: 'relative'}}>
                <Grid/>
                <div id="posTop" style={{top: '-20px'}}>
                    {xVals.map(v => <div key={v} onClick={() => select(v, 0, 2)} className="markerTop clickable" style={{left: (v + 8) * glob.unit + 'px', top: (glob.rotated ? 1 : -1) * xLevels.levelByValue.get(v) * TOP_ROW_HEIGHT + 'px'}}>{v}</div>)}
                    {xVals2.length > 0 && !hHidden && <div className="markerTop" style={glob.rotated ? {right: '0px'} : {left: '5px'}}>H</div>}
                </div>
                {xVals2.length > 0 && <div id="posBottom" style={{bottom: '-20px'}}>
                    {xVals2.map(v => <div key={v} onClick={() => select(v, 0, 1)} className="markerTop followers clickable" style={{left: (v + 8) * glob.unit + 'px', bottom: (glob.rotated ? 1 : -1) * x2Levels.levelByValue.get(v) * TOP_ROW_HEIGHT + 'px'}}>{v}</div>)}
                    {!dHiddenBottom && <div className="markerTop" style={glob.rotated ? {right: '0px'} : {left: '5px'}}>D</div>}
                </div>}
                <div id="posLeft" style={{left: '-50px'}}>
                    {yVals.map(v => <div key={v} onClick={() => select(v, 1, 2)} className="markerLeft clickable" style={{top: (v + 8) * glob.unit + 'px'}}>{v}</div>)}
                    {yVals2.length > 0 && !hHidden && <div className="markerLeft" style={glob.rotated ? {bottom: '0px'} : {top: '5px'}}>H</div>}
                </div>
                {yVals2.length > 0 && <div id="posLeft" style={{left: '-75px'}}>
                    {yVals2.map(v => <div key={v} onClick={() => select(v, 1, 1)} className="markerLeft clickable" style={{top: (v + 8) * glob.unit + 'px'}}>{v}</div>)}
                    {!dHiddenLeft && <div className="markerLeft" style={glob.rotated ? {bottom: '0px'} : {top: '5px'}}>D</div>}
                </div>}
                <div id="pointWrapper">
                    {currBild.leaders.map((p, idx) => <Point key={idx} pos={idx} part={2}/>)}
                    {followers && followers.map((p, idx) => <Point key={idx} pos={idx} part={1}/>)}
                </div>
                <button style={btnStyle} onClick={() => dispatch(setRotated(!glob.rotated))}>⥁</button>
            </div>
            <div className="bildSidePanel" style={{marginTop: '50px', verticalAlign: 'top'}}>
                <div className="bildControls" style={{flexDirection: 'column', verticalAlign: 'top'}}>
                    {glob.editMode === 1 && <Wheel/>}
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'}}>
                        {currBildIdx > 0 && <button style={{marginTop: '10px', width: '75px'}} onClick={() => dispatch(setAnimated(!glob.animated))}>{glob.animated ? 'Animation stoppen' : 'Animation starten'}</button>}
                        {glob.editMode === 1 && (<>
                            <div>
                                <button onClick={copyFrom}>Übernehme Punkte aus Bild</button>
                                <MyInput style={{width: '30px', marginLeft: '5px'}} value={inp} set={setInp}/></div>
                            {sameX && <button onClick={() => openDialogView(<DistDlg axis={0}/>)}>Seitenabstände...</button>}
                            {sameY && <button onClick={() => openDialogView(<DistDlg axis={1}/>)}>Tiefenabstände...</button>}
                        </>)}
                        {selectedPos.length === 2 && <button onClick={swapPoints}>Tauschen</button>}
                        {selectedPos.length === 2 && <button onClick={swapPointsFromHere}>Tauschen ab hier</button>}
                    </div>
                </div>
                <div className="bildTableCol" style={{flexDirection: 'column', verticalAlign: 'top', gap: '10px'}}>
                    {glob.editMode === 0 && <div>{currBild.point}</div>}
                    <BildTable/>
                    {glob.editMode === 1 && <button onClick={toggleFollowers}>{currBild.followers ? 'Damenpunkte löschen' : 'Damenpunkte hinzufügen'}</button>}

                    {glob.editMode === 0 && <div style={{maxWidth: '250px'}}>{currBild.comment}</div>}
                    {glob.editMode === 1 && (<div>
                        Punkt ist <MyInput type="text" value={currBild.point} set={val => updateBild({...currBild, point: val})}/><br/><br/>
                        <textarea style={{width: "250px", height: '100px'}} value={currBild.comment ?? ''} onChange={e => updateBild({...currBild, comment: e.target.value})}/>
                    </div>)}
                </div>
            </div>

        </div>

    </div>)
}

const BildHeader = props => {
    const dispatch = useDispatch();
    const glob = {...useSelector(getPersistentStorage), ...useSelector(getSession)};
    const currBildIdx = glob.currBildIdx
    const currBild = glob.plan.bilder[currBildIdx];

    const handleCopyBild = (before) => {
        let newId = parseInt(glob.plan.maxId) + 1
        const newBild = {...currBild, id: newId};
        newBild.title += " - Kopie"
        const updatedBilder = [...glob.plan.bilder];

        if (before) {
            updatedBilder.splice(currBildIdx, 0, newBild);
            // currBildIdx bleibt gleich
        } else {
            updatedBilder.splice(currBildIdx + 1, 0, newBild);
            dispatch(setCurrBildIdx(currBildIdx + 1));
        }

        updatePlan({
            ...glob.plan,
            bilder: updatedBilder,
            changed: true,
            maxId: newId
        });
    };

    return (<div id="bildHeader" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px', gap: '5px'}}>
        <div style={{flex: 1}}>
            {currBildIdx > 0 && (
                <div className="arrLeft clickable" onClick={() => dispatch(setCurrBildIdx(currBildIdx - 1))}>
                    Bild {currBildIdx} {glob.plan.bilder[currBildIdx - 1].title}
                </div>
            )}
        </div>

        {glob.editMode === 1 && <button onClick={() => handleCopyBild(true)} style={{margin: '0 10px'}}>+</button>}

        <div style={{flex: 1}}>
            Bild {currBildIdx + 1} {glob.editMode ?
            <MyInput
                type="text"
                value={currBild.title}
                set={val => updateBild({...currBild, title: val})}
                className="bildTitleInput"/>
            : currBild.title}

            <button className={glob.editMode ? 'bildCloseBtn' : ''} onClick={() => dispatch(setEditMode(glob.editMode ? 0 : 1))} style={{marginLeft: '10px'}}>
                {glob.editMode ? '✕' : '✎'}
            </button>
        </div>

        {glob.editMode === 1 && <button onClick={() => handleCopyBild(false)} style={{margin: '0 10px'}}>+</button>}

        <div style={{flex: 1}}>
            {currBildIdx < glob.plan.bilder.length - 1 && (
                <div className="arrRight clickable" onClick={() => dispatch(setCurrBildIdx(currBildIdx + 1))}>
                    Bild {currBildIdx + 2} {glob.plan.bilder[currBildIdx + 1].title}
                </div>
            )}
        </div>
    </div>)
}

const Grid = props => {
    const glob = {
        ...useSelector(getPersistentStorage),
        ...useSelector(getSession),
    };

    const unit = glob.unit; // z. B. 50 → 50px pro Meter
    const fieldSize = 2 * unit; // 2 Meter pro Feld

    return (<table id="grid">
        <tbody>
        {Array.from({length: 8}, (_, rowIdx) => (
            <tr key={rowIdx}>
                {Array.from({length: 8}, (_, colIdx) => (
                    <td
                        key={colIdx}
                        style={{
                            width: `${fieldSize}px`,
                            height: `${fieldSize}px`,
                        }}
                    ></td>
                ))}
            </tr>
        ))}
        </tbody>
    </table>)
}
